// Measurement unit definitions and filtering logic

export interface UnitDefinition {
  value: string;
  label: string;
  system: 'metric' | 'imperial' | 'universal';
  category: 'weight' | 'volume' | 'count';
}

// Complete unit definitions with proper categorization
export const ALL_UNITS: UnitDefinition[] = [
  // Universal units (available in both systems)
  { value: 'pieces', label: 'Pieces', system: 'universal', category: 'count' },
  { value: 'pack', label: 'Pack', system: 'universal', category: 'count' },
  { value: 'box', label: 'Box', system: 'universal', category: 'count' },
  { value: 'can', label: 'Can', system: 'universal', category: 'count' },
  { value: 'bottle', label: 'Bottle', system: 'universal', category: 'count' },
  { value: 'bag', label: 'Bag', system: 'universal', category: 'count' },
  { value: 'loaf', label: 'Loaf', system: 'universal', category: 'count' },
  { value: 'carton', label: 'Carton', system: 'universal', category: 'count' },
  
  // Metric units
  { value: 'g', label: 'Grams', system: 'metric', category: 'weight' },
  { value: 'kg', label: 'Kilograms', system: 'metric', category: 'weight' },
  { value: 'ml', label: 'Milliliters', system: 'metric', category: 'volume' },
  { value: 'l', label: 'Liters', system: 'metric', category: 'volume' },
  
  // Imperial units
  { value: 'lbs', label: 'Pounds', system: 'imperial', category: 'weight' },
  { value: 'oz', label: 'Ounces', system: 'imperial', category: 'weight' },
  { value: 'cups', label: 'Cups', system: 'imperial', category: 'volume' },
  { value: 'tbsp', label: 'Tablespoons', system: 'imperial', category: 'volume' },
  { value: 'tsp', label: 'Teaspoons', system: 'imperial', category: 'volume' },
  { value: 'fl oz', label: 'Fluid Ounces', system: 'imperial', category: 'volume' },
  { value: 'gal', label: 'Gallons', system: 'imperial', category: 'volume' },
  { value: 'qt', label: 'Quarts', system: 'imperial', category: 'volume' },
  { value: 'pt', label: 'Pints', system: 'imperial', category: 'volume' },
];

/**
 * Filter units based on user's measurement system preference
 */
export const getUnitsForSystem = (measurementSystem: 'metric' | 'imperial'): UnitDefinition[] => {
  return ALL_UNITS.filter(unit => 
    unit.system === 'universal' || unit.system === measurementSystem
  );
};

/**
 * Get just the unit values (for backward compatibility)
 */
export const getUnitValues = (measurementSystem: 'metric' | 'imperial'): string[] => {
  return getUnitsForSystem(measurementSystem).map(unit => unit.value);
};

/**
 * Convert a unit from one system to another (basic conversions)
 */
export const convertUnit = (
  value: number, 
  fromUnit: string, 
  toSystem: 'metric' | 'imperial'
): { value: number; unit: string } => {
  // Simple conversion rules - could be expanded later
  const conversions: Record<string, Record<string, { factor: number; unit: string }>> = {
    // Weight conversions
    'g': {
      imperial: { factor: 0.035274, unit: 'oz' },
    },
    'kg': {
      imperial: { factor: 2.20462, unit: 'lbs' },
    },
    'oz': {
      metric: { factor: 28.3495, unit: 'g' },
    },
    'lbs': {
      metric: { factor: 0.453592, unit: 'kg' },
    },
    // Volume conversions
    'ml': {
      imperial: { factor: 0.033814, unit: 'fl oz' },
    },
    'l': {
      imperial: { factor: 0.264172, unit: 'gal' },
    },
    'fl oz': {
      metric: { factor: 29.5735, unit: 'ml' },
    },
    'cups': {
      metric: { factor: 236.588, unit: 'ml' },
    },
  };

  const conversion = conversions[fromUnit]?.[toSystem];
  
  if (conversion) {
    return {
      value: Math.round(value * conversion.factor * 100) / 100, // Round to 2 decimal places
      unit: conversion.unit
    };
  }

  // If no conversion available, return original
  return { value, unit: fromUnit };
};

/**
 * Smart unit suggestion based on quantity and category
 */
export const suggestUnit = (
  quantity: number, 
  measurementSystem: 'metric' | 'imperial',
  category?: 'weight' | 'volume' | 'count'
): string => {
  const availableUnits = getUnitsForSystem(measurementSystem);
  
  // For counts, always suggest pieces
  if (category === 'count' || quantity === Math.floor(quantity)) {
    return 'pieces';
  }
  
  if (measurementSystem === 'metric') {
    if (category === 'weight') {
      return quantity >= 1000 ? 'kg' : 'g';
    }
    if (category === 'volume') {
      return quantity >= 1000 ? 'l' : 'ml';
    }
  } else {
    if (category === 'weight') {
      return quantity >= 16 ? 'lbs' : 'oz';
    }
    if (category === 'volume') {
      return quantity >= 8 ? 'cups' : 'fl oz';
    }
  }
  
  return 'pieces'; // Default fallback
};

/**
 * Check if a unit is valid for the given measurement system
 */
export const isValidUnitForSystem = (unit: string, measurementSystem: 'metric' | 'imperial'): boolean => {
  const validUnits = getUnitValues(measurementSystem);
  return validUnits.includes(unit);
};

/**
 * Get display label for a unit
 */
export const getUnitLabel = (unitValue: string): string => {
  const unit = ALL_UNITS.find(u => u.value === unitValue);
  return unit?.label || unitValue;
};

/**
 * Body stats conversion functions
 */

// Height conversions
export const convertHeight = {
  cmToFeetInches: (cm: number): { feet: number; inches: number } => {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return { feet, inches };
  },
  
  feetInchesToCm: (feet: number, inches: number): number => {
    return Math.round(((feet * 12) + inches) * 2.54);
  },
  
  formatHeight: (cm: number, system: 'metric' | 'imperial'): string => {
    if (system === 'imperial') {
      const { feet, inches } = convertHeight.cmToFeetInches(cm);
      return `${feet}'${inches}"`;
    }
    return `${cm} cm`;
  }
};

// Weight conversions
export const convertWeight = {
  kgToLbs: (kg: number): number => {
    return Math.round(kg * 2.20462 * 10) / 10; // Round to 1 decimal place
  },
  
  lbsToKg: (lbs: number): number => {
    return Math.round(lbs / 2.20462 * 10) / 10; // Round to 1 decimal place
  },
  
  formatWeight: (kg: number, system: 'metric' | 'imperial'): string => {
    if (system === 'imperial') {
      return `${convertWeight.kgToLbs(kg)} lbs`;
    }
    return `${kg} kg`;
  }
};

/**
 * Get appropriate placeholders and labels for body stats based on measurement system
 */
export const getBodyStatsLabels = (measurementSystem: 'metric' | 'imperial') => {
  if (measurementSystem === 'imperial') {
    return {
      height: {
        label: 'Height',
        placeholder: 'e.g. 5\' 8"',
        unit: 'ft/in'
      },
      weight: {
        label: 'Weight',
        placeholder: 'e.g. 150',
        unit: 'lbs'
      }
    };
  }
  
  return {
    height: {
      label: 'Height',
      placeholder: 'e.g. 170',
      unit: 'cm'
    },
    weight: {
      label: 'Weight',
      placeholder: 'e.g. 70',
      unit: 'kg'
    }
  };
}; 